/**
 * Detects whether the app is running as an installed PWA
 * (standalone display mode), vs a regular browser tab.
 *
 * - Chromium / Android / desktop: CSS media `(display-mode: standalone)`
 *   matches when launched from the home screen / app window.
 * - iOS Safari: uses the non-standard `navigator.standalone` flag
 *   (Apple does not expose display-mode the same way on older iOS).
 */
export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || ('standalone' in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone))
  );
}

/**
 * True on iPhone / iPad / iPod (including iPadOS desktop UA with touch).
 * Used for Home Screen install guidance copy.
 */
export function isAppleTouchDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

export type PushPlatform = 'IOS' | 'ANDROID' | 'OTHER';

/**
 * Normalized platform for Web Push subscription registration.
 * iOS gets soft classification feedback (no Yes/No actions).
 */
export function detectPushPlatform(): PushPlatform {
  if (typeof navigator === 'undefined') return 'OTHER';
  if (isAppleTouchDevice()) return 'IOS';
  const ua = navigator.userAgent || '';
  if (/Android/i.test(ua)) return 'ANDROID';
  return 'OTHER';
}
